import { useRef } from 'react'
import { Paperclip, X } from 'lucide-react'

import {
  HELP_ATTACHMENT_ACCEPT,
  helpAttachmentError,
  type HelpSupportAttachment,
} from '@/services/help-support'

const formatSize = (bytes: number) => (bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  : `${Math.max(1, Math.round(bytes / 1024))} KB`)

/** Files sent with a help request or reply, as links that open in a new tab. */
export function HelpAttachmentLinks({ attachments, className = '' }: {
  attachments: HelpSupportAttachment[] | undefined
  className?: string
}) {
  if (!attachments?.length) return null
  return (
    <div className={`mt-2 flex flex-wrap gap-1.5 ${className}`}>
      {attachments.map((attachment) => {
        const label = (
          <>
            <Paperclip className="h-3 w-3 shrink-0" />
            <span className="max-w-[180px] truncate">{attachment.name}</span>
            <span className="opacity-70">{formatSize(attachment.size)}</span>
          </>
        )
        return attachment.url ? (
          <a
            key={attachment.id}
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-current/20 bg-black/5 px-2 py-0.5 text-[11px] underline-offset-2 hover:underline"
          >
            {label}
          </a>
        ) : (
          <span key={attachment.id} className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-[11px]" title="Link unavailable">
            {label}
          </span>
        )
      })}
    </div>
  )
}

/** "Attach files" button plus the chosen files, each removable. */
export function HelpAttachmentPicker({ files, onChange, onError, disabled = false }: {
  files: File[]
  onChange: (files: File[]) => void
  onError: (message: string) => void
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  const add = (picked: FileList | null) => {
    const next = [...files, ...Array.from(picked ?? [])]
    const error = helpAttachmentError(next)
    if (error) onError(error)
    else onChange(next)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={HELP_ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(event) => add(event.target.files)}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-muted-foreground hover:bg-black/5 hover:text-foreground disabled:opacity-50"
      >
        <Paperclip className="h-3.5 w-3.5" />
        Attach files
      </button>
      {files.map((file, index) => (
        <span key={`${file.name}-${index}`} className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-[11px]">
          <span className="max-w-[160px] truncate">{file.name}</span>
          <span className="opacity-70">{formatSize(file.size)}</span>
          <button
            type="button"
            aria-label={`Remove ${file.name}`}
            disabled={disabled}
            onClick={() => onChange(files.filter((_, i) => i !== index))}
            className="rounded-full p-0.5 hover:bg-black/10"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  )
}
